import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faChevronDoubleDown,
  faChevronDoubleUp,
  faGift,
  faTrophy,
} from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { StreakFire } from '@sorare/core/src/atoms/icons/StreakFire';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, BrandM } from '@sorare/core/src/atoms/typography';
import { useIsMobile } from '@sorare/core/src/hooks/device/useIsMobile';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { GameweekDate } from '#src/components/so5/LastFixtureStory/GameweekDate/index.tsx';
import type { StoryState } from '#src/components/so5/LastFixtureStory/types.ts';

import type { Title_so5Fixture } from './__generated__/index.graphql.ts';

const TitleRow = styled(Horizontal).attrs({ gap: 0 })`
  padding: 0;
  gap: var(--double-unit);
  @media ${tabletAndAbove} {
    gap: 80px;
  }
`;

const titles: { [key in StoryState]?: MessageDescriptor } = defineMessages({
  promotion: {
    defaultMessage: 'You got promoted',
    id: 'LastFixtureStoryStep.promotion',
  },
  relegation: {
    defaultMessage: 'You can do better!',
    id: 'LastFixtureStoryStep.relegation',
  },
  cardLeveledUp: {
    defaultMessage: 'Leveled up!',
    id: 'LastFixtureStoryStep.leveledUp',
  },
  completedStreakTask: {
    defaultMessage: 'Streaks',
    id: 'LastFixtureStoryStep.streaks',
  },
  rewardsRecap: {
    defaultMessage: 'Rewards Recap',
    id: 'LastFixtureStoryStep.recap',
  },
  cardReveal: {
    defaultMessage: 'Reward time',
    id: 'LastFixtureStoryStep.rewards',
  },
});
const greenGradient = 'linear-gradient(90deg, #22ff60 0%, #14993a 100%)';
const dateBg: { [key in StoryState]?: string } = {
  promotion: greenGradient,
  relegation: 'linear-gradient(90deg, #f23b27 0%, #9d190a 100%)',
  cardLeveledUp: greenGradient,
  completedStreakTask: 'linear-gradient(323deg, #FEF314 -3.1%, #FF4E22 52.7%)',
  rewardsRecap: greenGradient,
  cardReveal: 'var(--c-score-high)',
};
const icons: { [key in StoryState]?: ReactNode } = {
  promotion: (
    <FontAwesomeIcon
      icon={faChevronDoubleUp}
      color="var(--c-score-high)"
      size="xl"
    />
  ),
  relegation: (
    <FontAwesomeIcon
      icon={faChevronDoubleDown}
      color="var(--c-red-400)"
      size="xl"
    />
  ),
  cardLeveledUp: (
    <FontAwesomeIcon
      icon={faChevronDoubleUp}
      color="var(--c-score-high)"
      size="xl"
    />
  ),
  completedStreakTask: <StreakFire />,
  rewardsRecap: (
    <FontAwesomeIcon icon={faGift} color="var(--c-white)" size="xl" />
  ),
  cardReveal: (
    <FontAwesomeIcon icon={faTrophy} color="var(--c-white)" size="xl" />
  ),
};

type Props = {
  type: StoryState;
  so5Fixture?: Title_so5Fixture;
};
export const Title = ({ type, so5Fixture }: Props) => {
  const isMobile = useIsMobile();

  const TitleTypo = isMobile ? BrandM : BrandL;

  return (
    <Vertical center>
      {!!so5Fixture && (
        <GameweekDate
          startDate={so5Fixture.startDate}
          endDate={so5Fixture.endDate}
          colors={{
            bg: dateBg[type] || greenGradient,
            fg: 'var(--c-black)',
          }}
        />
      )}
      {!!titles[type] && !!icons[type] && (
        <TitleRow>
          {icons[type]}
          <TitleTypo className="text-center">
            <FormattedMessage {...titles[type]} />
          </TitleTypo>
          {icons[type]}
        </TitleRow>
      )}
    </Vertical>
  );
};

Title.fragments = {
  so5Fixture: gql`
    fragment Title_so5Fixture on So5Fixture {
      slug
      startDate
      endDate
    }
  ` as TypedDocumentNode<Title_so5Fixture>,
};
